import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef } from '@mui/x-data-grid';		//https://mui.com/x/react-data-grid/ [MIT version]
import { Box, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, MenuItem } from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/Receipt';

import Api from '../../Services/Api.js';
import { PAGE_SIZE, pageSizeOptions, timeTableIntervals as optionsTimeTableIntervals } from '../../Config/constants.js';
import moment from 'moment';
import { Link } from 'react-router-dom';
import ActionButton from '../ActionButton.js';

function ReportCustomersToInvoice() {

	const apiRef = useGridApiRef();	//https://mui.com/x/api/data-grid/
	const DEFAULT_VALUE = "0";

	const [rows, setRows] = useState([]);
	const [displayAlert, setDisplayAlert] = useState(0);
	const [displayDialog, setDisplayDialog] = useState(0);
	const [displayDialogContent, setDisplayDialogContent] = useState(null);
	const [lastUpdate, setLastUpdate] = useState(null);
	const [days, setDays] = useState(DEFAULT_VALUE);

	const columns = [
		{
			field: 'customer',
			headerName: 'Cliente',
			flex: 1,
			valueGetter: (params) => {
				return `${params.row.surname} ${params.row.name}`;
			},
		},
		{
			field: 'installation',
			headerName: 'Impianto',
			flex: 2,
			valueGetter: (params) => {
				return `${params.row.brand} ${params.row.model} ${params.row.unique_code}`;
			},
		},
		{
			field: 'appointment_at',
			headerName: 'Appuntamento',
			flex: 2,
			type: 'date',
			valueGetter: (params) => params.row.appointment_at ? new Date(params.row.appointment_at) : null,
			valueFormatter: (params) => params.value ? params.value.toLocaleDateString('it-IT') : '',
		},
		{
			field: 'price',
			headerName: 'Importo',
			type: 'number',
			valueFormatter: (params) => params.value != null ? new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : '',
		},       
		{
			field: 'id',
			headerName: 'Azioni',
			flex: 1,
			renderCell: (params) => {
				function onShow(e) {
					e.stopPropagation(); // don't select this row after clicking
					setDisplayDialogContent(popupContent(params.row));
					setDisplayDialog(1);
				};
				function approveInvoice(e) {
					e.stopPropagation(); // don't select this row after clicking
                    if (window.confirm("Confermare la Fatturazione del cliente indicato?")) {
						approveInvoices([params.value]);
					}
				};
				return <>
					<ActionButton title={"Visualizza dettagli di fatturazione"} onClick={onShow}>
						<InfoIcon />
					</ActionButton>
					<ActionButton title={"Conferma fatturazione"} onClick={approveInvoice}>
						<ReceiptIcon />
					</ActionButton>
				</>
			}
		},
	]

	const handleClose = () => {
		setDisplayDialog(0);
		setDisplayDialogContent(null);
	};

	function arrayIntervalsResponse() {
		return (
			[DEFAULT_VALUE, ...optionsTimeTableIntervals].map((interval) => (
				<MenuItem key={interval} value={interval}>
					{interval === '0' ? 'Oggi' : `${interval} giorni`}
				</MenuItem>
			))
		);
	}

	function extraFilter(selectedValue) {
		setDays(selectedValue);
	}

	function approveInvoices(ids) {
		try {
			Api.post({endpoint: `report/approve-invoice`, body: {"list": ids}, contentType : 'application/json'})
				.then(response => {
					setDisplayDialog(1);
					setDisplayDialogContent(null);
					setLastUpdate(Date.now()+1);
				})
				.catch(error => {
					console.log(JSON.stringify(error));
					setDisplayDialog(-1);
				});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayDialog(-1);
		}
	};

	useEffect(() => {
		try {
			Api.get({endpoint: `report/customers-to-invoice/${days}`, contentType : 'application/json'})
			.then(response => {
				setRows(response.data.list);
			})
			.catch(error => {
				console.log(JSON.stringify(error));
				setDisplayAlert(-1);
			});
		} catch (error) {
			console.log(JSON.stringify(error));
			setDisplayAlert(-1);
		}
	}, [lastUpdate, days]);	// eslint-disable-line react-hooks/exhaustive-deps

	const theme = createTheme({
			palette: {
				primary: { main: '#11cb5f' },
			},
		},
		itIT,
	);

	const popupContent = (row) => {
		return <>
			<ul style={{listStyleType: 'none', paddingLeft: '0'}}>
				<li>Cliente: <strong>{`${row.surname} ${row.name}`}</strong></li>
				<li>Codice Fiscale/P.Iva: <strong>{row.fiscalVatCode}</strong></li>
				<li>Indirizzo: <strong>{row.address}{row.number ? ', '+row.number : ''} - {`${row.city} ${row.zipCode} (${row.area})`}</strong></li>
			</ul>
		</>;
	};

	  
	return (
		<>
			<h5>Report Clienti da Fatturare</h5>

			<div className="row styled">
				<div className="col-md-2">
					<TextField
						fullWidth
						label="Intervallo di visualizzazione"
						name="interval"
						className="styled"
						select
						defaultValue = {DEFAULT_VALUE}
						InputProps={{
							style: {color: theme.palette.primary.darkMain}
						}}
						onChange={(e) => {
							extraFilter(e.target.value);
						}}
					>
						{arrayIntervalsResponse()}
					</TextField>
				</div>
				<div className="col-md-10">
					<p style={{fontSize: '0.8em'}}>
						{days && <span>Dal: {moment().subtract(days, 'days').format('DD/MM/YYYY')} - Al: {moment().format('DD/MM/YYYY')}</span>}
					</p>
				</div>
			</div>

			<Box sx={{ width: '100%', minWidth: '800px' }}>
				<ThemeProvider theme={theme}>
					<DataGrid
						sx={{ minHeight: '300px' }}
						rows={rows}
						columns={columns}						
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: PAGE_SIZE,
								},
							},
						}}
						apiRef={apiRef}
						slots={{
							toolbar: GridToolbar,
						}}
						slotProps={{
							columnsPanel: {
								disableHideAllButton: true,
							},
							toolbar: {
								showQuickFilter: true,
							},
						}}
						pageSizeOptions={pageSizeOptions}
					/>
					<div className="mt-2">
						<Link to={'/cms/reports'} >
							<Button variant="contained">Torna alla lista</Button>
						</Link>
					</div>
				</ThemeProvider>
				{displayAlert === -1 && (
					<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
				)}
			</Box>

			<Dialog
				open={displayDialog !== 0}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				{displayDialogContent && displayDialog !== -1? (
					<>
						<DialogTitle id="alert-dialog-title">
							Dettagli di fatturazione
						</DialogTitle>
						<DialogContent>
							<DialogContentText id="alert-dialog-description">
								<div>{displayDialogContent}</div>
							</DialogContentText>
						</DialogContent>
					</>
				) : (
					<>
						<DialogTitle id="alert-dialog-title">
							{displayDialog === 1 && "Operazione completata"}
							{displayDialog === -1 && "Errore"}
						</DialogTitle>
						<DialogContent>
								<DialogContentText id="alert-dialog-description">
									{displayDialog === 1 && "Operazione completata con successo!"}
									{displayDialog === -1 && "Si è verificato un errore inatteso"}
								</DialogContentText>
						</DialogContent>
					</>
				)}
				<DialogActions>
					<Button onClick={handleClose}>Chiudi</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ReportCustomersToInvoice;